import { Localized, useLocalization } from "@fluent/react";
import { Box, Button, Divider, ListItemText, Menu, MenuItem, MenuList, Select, FormControl, InputLabel } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';

type MonthTypes = "current" | "previous" | "twoMonthsBack" | "custom";

interface MonthFilterMenuProps {
    currentOption?: MonthTypes;
    initialDateFrom?: Date | null;
    initialDateTo?: Date | null;
    onSubmit: (date: { dateFrom: Date, dateTo: Date }) => void;
}

const MONTH_KEYS = [
    'january',
    'february',
    'march',
    'april',
    'may',
    'june',
    'july',
    'august',
    'september',
    'october',
    'november',
    'december',
] as const;

const capitalizeFirst = (text: string): string => text.charAt(0).toUpperCase() + text.slice(1);

const getStartOfMonth = (date: Date): Date => {
    return new Date(date.getFullYear(), date.getMonth(), 1);
};

const getEndOfMonth = (date: Date): Date => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59);
};

const getMonthsArray = (monthNames: string[]) => {
    const months: { value: number; label: string }[] = [];
    for (let i = 0; i < 12; i++) {
        months.push({ value: i, label: capitalizeFirst(monthNames[i]) });
    }
    return months;
};

export const MonthFilterMenu = ({ currentOption, initialDateFrom, initialDateTo, onSubmit }: MonthFilterMenuProps) => {
    const { l10n } = useLocalization();
    const currentDate = useMemo(() => new Date(), []);

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const openMenu = Boolean(anchorEl);

    const [type, setType] = useState<MonthTypes | "custom">(currentOption ?? 'current');
    const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
    const [selectedMonth, setSelectedMonth] = useState(currentDate.getMonth());

    useEffect(() => {
        if (currentOption !== undefined) setType(currentOption);
    }, [currentOption]);

    useEffect(() => {
        if (initialDateFrom && initialDateTo) {
            const currentMonthStart = getStartOfMonth(currentDate);
            const currentMonthEnd = getEndOfMonth(currentDate);
            
            const prevMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
            const prevMonthStart = getStartOfMonth(prevMonth);
            const prevMonthEnd = getEndOfMonth(prevMonth);
            
            const twoMonthsBack = new Date(currentDate.getFullYear(), currentDate.getMonth() - 2, 1);
            const twoMonthsBackStart = getStartOfMonth(twoMonthsBack);
            const twoMonthsBackEnd = getEndOfMonth(twoMonthsBack);

            if (initialDateFrom.getTime() === currentMonthStart.getTime() && 
                initialDateTo.getTime() === currentMonthEnd.getTime()) {
                setType('current');
            } else if (initialDateFrom.getTime() === prevMonthStart.getTime() && 
                      initialDateTo.getTime() === prevMonthEnd.getTime()) {
                setType('previous');
            } else if (initialDateFrom.getTime() === twoMonthsBackStart.getTime() && 
                      initialDateTo.getTime() === twoMonthsBackEnd.getTime()) {
                setType('twoMonthsBack');
            } else {
                setType('custom');
                setSelectedYear(initialDateFrom.getFullYear());
                setSelectedMonth(initialDateFrom.getMonth());
            }
        }
    }, [initialDateFrom, initialDateTo, currentDate]);

    const handleMonthClick = (monthType: MonthTypes) => {
        let dateFrom: Date;
        let dateTo: Date;

        switch (monthType) {
            case 'current':
                dateFrom = getStartOfMonth(currentDate);
                dateTo = getEndOfMonth(currentDate);
                break;
            case 'previous':
                const prevMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1);
                dateFrom = getStartOfMonth(prevMonth);
                dateTo = getEndOfMonth(prevMonth);
                break;
            case 'twoMonthsBack':
                const twoMonthsBack = new Date(currentDate.getFullYear(), currentDate.getMonth() - 2, 1);
                dateFrom = getStartOfMonth(twoMonthsBack);
                dateTo = getEndOfMonth(twoMonthsBack);
                break;
            default:
                dateFrom = getStartOfMonth(currentDate);
                dateTo = getEndOfMonth(currentDate);
        }

        setType(monthType);
        setSelectedYear(dateFrom.getFullYear());
        setSelectedMonth(dateFrom.getMonth());
        onSubmit({ dateFrom, dateTo });
        setAnchorEl(null);
    };

    const handleCustomMonthChange = () => {
        const dateFrom = getStartOfMonth(new Date(selectedYear, selectedMonth, 1));
        const dateTo = getEndOfMonth(new Date(selectedYear, selectedMonth, 1));
        setType('custom');
        onSubmit({ dateFrom, dateTo });
    };

    const getPreviousMonthDate = (monthsBack: number): Date => {
        return new Date(currentDate.getFullYear(), currentDate.getMonth() - monthsBack, 1);
    };

    const monthNames = useMemo(
        () => MONTH_KEYS.map((monthKey) => l10n.getString(monthKey, null, monthKey)),
        [l10n]
    );

    const getMonthName = (date: Date): string => {
        const monthName = monthNames[date.getMonth()] ?? '';
        return `${capitalizeFirst(monthName)} ${date.getFullYear()}`;
    };

    const currentMonthName = getMonthName(currentDate);
    const previousMonthDate = getPreviousMonthDate(1);
    const previousMonthName = getMonthName(previousMonthDate);
    const twoMonthsBackDate = getPreviousMonthDate(2);
    const twoMonthsBackName = getMonthName(twoMonthsBackDate);
    const customMonthDate = new Date(selectedYear, selectedMonth, 1);
    const customMonthName = getMonthName(customMonthDate);

    const months = getMonthsArray(monthNames);

    const years = Array.from({ length: 10 }, (_, i) => currentDate.getFullYear() - i);

    const getLabelText = () => {
        switch (type) {
            case 'current':
                return currentMonthName;
            case 'previous':
                return previousMonthName;
            case 'twoMonthsBack':
                return twoMonthsBackName;
            case 'custom':
                return customMonthName;
            default:
                return currentMonthName;
        }
    };

    return (
        <>
            <Button onClick={(event: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget)} variant="text" startIcon={<CalendarMonthIcon />}>
                {getLabelText()}
            </Button>
            <Menu anchorEl={anchorEl} open={openMenu} onClose={() => setAnchorEl(null)}>
                <MenuList>
                    <MenuItem onClick={() => handleMonthClick('current')}>
                        <ListItemText>
                            <Localized id="filters-date-current-month">
                                {currentMonthName}
                            </Localized>
                        </ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => handleMonthClick('previous')}>
                        <ListItemText>
                            <Localized id="filters-date-previous-month">
                                {previousMonthName}
                            </Localized>
                        </ListItemText>
                    </MenuItem>
                    <MenuItem onClick={() => handleMonthClick('twoMonthsBack')}>
                        <ListItemText>
                            {twoMonthsBackName}
                        </ListItemText>
                    </MenuItem>

                    <Divider />

                    <MenuItem>
                        <Box display="flex" flexDirection="column" width="100%" gap={2} sx={{ p: 1, minWidth: 250 }}>
                            <ListItemText>
                                <Localized id="filters-date-custom-date">
                                    <span style={{ fontWeight: 'bold' }}>_Własna data</span>
                                </Localized>
                            </ListItemText>

                             <FormControl fullWidth size="small">
                                <InputLabel id="month-select-label">
                                    <Localized id="month">_Miesiąc</Localized>
                                </InputLabel>
                                <Select
                                    labelId="month-select-label"
                                    value={selectedMonth}
                                    label={l10n.getString("month", null, "_Miesiąc")}
                                    onChange={(e) => {
                                        setSelectedMonth(e.target.value as number);
                                    }}
                                >
                                    {months.map((month) => (
                                        <MenuItem key={month.value} value={month.value}>
                                            {month.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl fullWidth size="small">
                                <InputLabel id="year-select-label">
                                    <Localized id="year">_Rok</Localized>
                                </InputLabel>
                                <Select
                                    labelId="year-select-label"
                                    value={selectedYear}
                                    label={l10n.getString("year", null, "_Rok")}
                                    onChange={(e) => {
                                        setSelectedYear(e.target.value as number);
                                    }}
                                >
                                    {years.map((year) => (
                                        <MenuItem key={year} value={year}>
                                            {year}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <Button
                                variant="contained"
                                color="primary"
                                size="small"
                                onClick={handleCustomMonthChange}
                            >
                                <Localized id="confirm">_Potwierdź</Localized>
                            </Button>
                        </Box>
                    </MenuItem>
                </MenuList>
            </Menu>
        </>
    );
};
